import { pusherService } from './pusherService'
import api from '@/config/api'
import { fixturePlaylistState, fixtureSchedulerState, fixtureRouterState, fixtureHeartbeat, fixtureRouterLabels } from './__fixtures__/agentGateway'

const AGENT_EVENTS = ['playlist_state', 'scheduler_state', 'router_state', 'heartbeat', 'router_labels', 'command_response']

const MOCK_ENABLED = import.meta.env.VITE_AGENT_GATEWAY_MOCK === 'true'

// Mock harness — only active when VITE_AGENT_GATEWAY_MOCK=true
class MockAgentGatewayClient {
  constructor() {
    this._intervals = []
    this._listeners = new Map()
    this._tickCount = 0
  }

  subscribe(stationId, onMessage) {
    let lastHeartbeat = Date.now()

    const heartbeatInterval = setInterval(() => {
      lastHeartbeat = Date.now()
      this._tickCount++

      // Emit heartbeat every tick (every 5s in mock)
      onMessage({ type: 'heartbeat', payload: { ...fixtureHeartbeat, uptime_secs: fixtureHeartbeat.uptime_secs + this._tickCount * 5 } })

      // Emit full state snapshots on first tick and every 10th tick
      if (this._tickCount === 1 || this._tickCount % 10 === 0) {
        onMessage({ type: 'playlist_state', payload: fixturePlaylistState })
        onMessage({ type: 'scheduler_state', payload: fixtureSchedulerState })
        onMessage({ type: 'router_state', payload: fixtureRouterState })
        onMessage({ type: 'router_labels', payload: fixtureRouterLabels })
      }
    }, 5000)

    this._intervals.push(heartbeatInterval)

    // Emit initial state immediately
    setTimeout(() => {
      onMessage({ type: 'heartbeat', payload: fixtureHeartbeat })
      onMessage({ type: 'playlist_state', payload: fixturePlaylistState })
      onMessage({ type: 'scheduler_state', payload: fixtureSchedulerState })
      onMessage({ type: 'router_state', payload: fixtureRouterState })
      onMessage({ type: 'router_labels', payload: fixtureRouterLabels })
    }, 200)

    return () => this._cleanup()
  }

  dispatchCommand(stationId, type, payload) {
    return api.post(`/v2/stations/${stationId}/agent/commands`, { type, payload }).then((res) => {
      const requestId = res.data?.request_id ?? res.data?.data?.request_id
      const listeners = this._listeners
      setTimeout(() => {
        const cb = listeners.get(requestId)
        if (cb) {
          cb({ type: 'command_response', payload: { success: true, command_type: type }, request_id: requestId })
          listeners.delete(requestId)
        }
      }, 500)
      return { request_id: requestId }
    })
  }

  onCommandResponse(requestId, cb) {
    this._listeners.set(requestId, cb)
  }

  _cleanup() {
    this._intervals.forEach(clearInterval)
    this._intervals = []
    this._tickCount = 0
  }
}

// Real client — uses pusherService private channel + HTTP commands
class RealAgentGatewayClient {
  constructor() {
    this._channelName = null
    this._channel = null
    this._commandListeners = new Map()
  }

  subscribe(stationId, onMessage) {
    this._channelName = `private-station-agent.${stationId}`

    // Ensure pusherService is connected
    pusherService.connect()

    this._channel = pusherService.subscribe(this._channelName)
    if (!this._channel) {
      console.warn('[AgentGatewayClient] Failed to subscribe to channel:', this._channelName)
      return () => {}
    }

    const normalize = (type) => (data) => {
      const payload = data?.payload ?? data
      const requestId = data?.request_id ?? null
      onMessage({ type, payload, request_id: requestId })

      // Resolve in-flight command if this is a command_response
      if (type === 'command_response' && requestId && this._commandListeners.has(requestId)) {
        this._commandListeners.get(requestId)({ type, payload, request_id: requestId })
        this._commandListeners.delete(requestId)
      }
    }

    AGENT_EVENTS.forEach((event) => {
      this._channel.bind(event, normalize(event))
    })

    return () => this._cleanup()
  }

  dispatchCommand(stationId, type, payload) {
    return api.post(`/v2/stations/${stationId}/agent/commands`, { type, payload }).then((res) => ({
      request_id: res.data?.request_id ?? res.data?.data?.request_id
    }))
  }

  onCommandResponse(requestId, cb) {
    this._commandListeners.set(requestId, cb)
  }

  _cleanup() {
    if (this._channelName) {
      pusherService.unsubscribe(this._channelName)
      this._channelName = null
      this._channel = null
    }
  }
}

const client = MOCK_ENABLED ? new MockAgentGatewayClient() : new RealAgentGatewayClient()

export default client
